import { SymmKey } from "src/common/keys";
import { FSWrappedKey, Node, FSRole } from "src/common/keys/protos/collections_pb";
import {
  Account, DriveEntryType, PathInfo, MaintainerPathNode, DriveTypeMapping, getDirSymmKey, GrantSet, decryptItemName, Helpers, ActiveGrantSet, PermissionSetType,
  PermissionSet, buildNavigationLink, DriveConstants, DriveConstantsType, buildWatchedNavigationLink, DirPathNode, decryptRootDirName, DefaultRoutes,
  GrantSetType, FullNodePath, DriveEntryTypes, PathNode, RootInfo
} from "src/common";
import _ from "lodash";


// Description: builds the set of paths for a node 
export class PathList {
  path_list!: PathNode[];
  constructor(
    public collection_id: string,
    path_list: DirPathNode[],
    public default_collection_id: string = "", // NOTE: This is the root directory id of the default collection 
    public drive_module: DriveConstantsType = DriveConstants.DEFAULT_DRIVE,
    public last_node_is_link: boolean = false // NOTE: This is needed for when we getPath using LINK_id and LINK_collection_id, reinforce type LINK
    // When getPath using LINK all the data comes back in the full_path_list.getPathList() - there is no type in PathNode (needs to be inferred)
  ) {
    this.path_list = this._buildPathNodes(path_list);
  }

  // Description: Loop through the DirPathNode Array and set the information for each PathNode
  private _buildPathNodes(path_list: DirPathNode[]): PathNode[] {
    const is_default_path = !!this.default_collection_id;
    return _.map(path_list, (path_node: DirPathNode, i: number) => {
      const is_last_link = path_list.length === Number(i + 1) && is_default_path;
      const path = new Path(this.collection_id, path_node,
        (is_last_link || this.last_node_is_link) ? DriveEntryType.LINK : DriveEntryType.DIR,
        this.default_collection_id,
        this.drive_module).node;
      const parent_node = i > 0 ? path_list[i - 1] : undefined;
      return { ...path, ...{ parent_node } };
    });
  }

  // Description: Initialize the PathList when the FullNodePath is returned from GetPath
  // join the 2 arrays and adjust for Link nodes and following Root node when needed
  // When using a link entity to get path the path will be returned in dir_path_list - so we need to pass that information to the last PathNode
  static initFullNodePath(
    collection_id: string,
    full_path_list: FullNodePath,
    root_info?: RootInfo,
    drive_module: DriveConstantsType = DriveConstants.DRIVE,
    last_node_is_link: boolean = false) {
    const default_path_list = !!root_info ? new PathList(root_info.collection_id, full_path_list.getDefaultCollectionPathList(), root_info.id, drive_module, last_node_is_link).path_list : [];
    const dir_path_list = new PathList(collection_id, full_path_list.getPathList(), undefined, drive_module, last_node_is_link).path_list;
    // NOTE: if there are nodes in the default_path_list remove the top level root node from dir_path_list -  
    // the last node in default_path_list is the link, the first in the dir_path_list is the root node
    // We need the link node for  DriveConstants.DRIVE module and the Root Node for DriveConstants.WATCHED
    if (default_path_list.length > 0) {
      drive_module === DriveConstants.WATCHED ? default_path_list.pop() : dir_path_list.shift();
    }
    return default_path_list.concat(dir_path_list);
  }


  // Description: Build breadcrumbs PathInfo[]
  static async buildPathInfoFromNodes(list: PathNode[], current_account: Account, permissions: PermissionSetType, active_grants: ActiveGrantSet):
    Promise<PathInfo[]> {
    const permission_set = PermissionSet.init(permissions);
    const paths = await Promise.all(_.map(list, async (node: PathNode, i: number) => {
      const wrapped_name = node.wrapped_name;
      const parent_node = i > 0 ? list[i - 1] : null;
      const parent_wrapped_dir_key = !!parent_node ? parent_node.wrapped_dir_key : null;
      const maintainer = !!parent_node ? parent_node : node;
      const current_grants = !!maintainer.maintainer_id && Object.prototype.hasOwnProperty.call(active_grants, maintainer.maintainer_id) ?
        active_grants[maintainer.maintainer_id] : null;
      let key_version = !!node.maintainer_node ? node.maintainer_node.key_version : maintainer?.key_version;
      const grant = !!current_grants ? GrantSet.getGrantByRole(current_grants, key_version, Node.ACLRole.READER) : null;
      const reader_user_key = !!grant ? await grant.key(current_account) : undefined;
      let path = DriveTypeMapping.DIR;
      try {
        if (!!parent_node) {
          const symmetric_key = !!parent_wrapped_dir_key ? await getDirSymmKey(parent_wrapped_dir_key, current_account, permissions, reader_user_key) : null;
          path = !!symmetric_key ? await decryptItemName(wrapped_name, symmetric_key, reader_user_key) : DriveTypeMapping.DIR;
        } else {
          key_version = !!node.root_dir_name ? node.root_dir_name.key_version : key_version;
          const permission = permission_set.permission(FSRole.READER, key_version) || permission_set.permission(FSRole.ACCESS, key_version);
          const user_key = !!permission ? await permission.key(current_account) : null;
          // NOTE: Handle root node names (This is important when a collection is renamed because other names will be stale)
          // NOTE: Try to get the wrapped name if the root_dir_name  fails
          path = (!!node.root_dir_name?.content && !!user_key) ? await decryptRootDirName(node.root_dir_name.content, permissions, current_account, key_version, { wrapped_name, key_version: node.key_version }) :
            (!!node.maintainer_node?.content && !!reader_user_key) ? Helpers.utf8Decode(await reader_user_key.encryption_key.unseal(node.maintainer_node.content)) :
              path = !!user_key ? Helpers.utf8Decode(await user_key?.encryption_key.unseal(wrapped_name)) : DriveTypeMapping.DIR;
        }
        return {
          path,
          url: node.url,
          id: node.id
        };
      } catch (error: unknown) {
        return {
          path: DriveTypeMapping.DIR,
          url: node.url,
          id: node.id
        };
      }
    }));
    return paths;
  }

  // Description: Build breadcrumbs PathInfo per PathNode
  static async buildPathInfoFromPathNode(node: PathNode, current_account: Account, permissions: PermissionSetType, grant_set?: GrantSetType):
    Promise<PathInfo> {
    const wrapped_name = node.wrapped_name;
    const permission_set = PermissionSet.init(permissions);
    let path = DriveTypeMapping.DIR;
    const parent_node = node.parent_node;
    const parent_wrapped_dir_key = !!parent_node ? parent_node.getWrappedDirKey() : null;
    let key_version = (node.maintainer_id === node.id && !!node.maintainer_node) ? (node.maintainer_node.key_version || 0) :
      !!parent_node ? (parent_node.getWrappedDirKey()?.getKeyVersion() || 0) : (node.key_version || 0);
    const grant = !!grant_set ? GrantSet.getGrantByRole(grant_set, key_version) : undefined;
    const reader_user_key = !!grant ? await grant.key(current_account) : undefined;
    try {
      if (node.maintainer_id === node.id && !!node.maintainer_node || !!node.root_dir_name) {
        key_version = !!node.root_dir_name ? (node.root_dir_name.key_version || 0) : key_version;
        const permission = permission_set.permission(FSRole.READER, key_version) || permission_set.permission(FSRole.ACCESS, key_version);
        const user_key = !!permission ? await permission.key(current_account) : null;
        // NOTE: Handle root node names (This is important when a collection is renamed because other names will be stale)
        //        Try to get the wrapped name if the root_dir_name  fails
        path = (!!node.root_dir_name?.content && !!user_key) ? await decryptRootDirName(node.root_dir_name.content, permissions, current_account, key_version, { wrapped_name, key_version: node.key_version }) :
          (!!node.maintainer_node?.content && !!reader_user_key) ? Helpers.utf8Decode(await reader_user_key.encryption_key.unseal(node.maintainer_node.content)) :
            path = !!user_key ? Helpers.utf8Decode(await user_key?.encryption_key.unseal(wrapped_name)) : DriveTypeMapping.DIR;
      } else if (!!parent_node) {
        const symmetric_key = !!parent_wrapped_dir_key ? await getDirSymmKey(parent_wrapped_dir_key, current_account, permissions, reader_user_key) : null;
        path = !!symmetric_key ? await decryptItemName(wrapped_name, symmetric_key, reader_user_key) : DriveTypeMapping.DIR;
      }
      return {
        path,
        url: node.url,
        id: node.id
      };
    } catch (error: unknown) {
      return {
        path: DriveTypeMapping.DIR,
        url: node.url,
        id: node.id
      };
    }
  }


  // Description: Build breadcrumbs PathInfo[] for Collection Server
  static buildPathInfoFromFilesyncPaths(list: PathInfo[], collection_id: string): PathInfo[] {
    const path_list = _.map(list, (info: PathInfo, i: number) => {
      return {
        ...info,
        ...{ url: buildNavigationLink(collection_id, info.id, DriveEntryType.DIR) }
      };
    });
    return path_list;
  }

  // Description: filter part of the path info tree (watched items)
  static filterNodes(path_infos: PathInfo[], include_nodes: string[]): PathInfo[] {
    return _.filter(path_infos, (path_info: PathInfo) => include_nodes.includes(path_info.id));
  }
}

// Description: builds the single path in the Path_list
export class Path implements PathNode {
  id!: string;
  path!: string;
  wrapped_name: Uint8Array;
  wrapped_dir_key?: FSWrappedKey;
  key_version?: number;
  deleted?: boolean;
  url!: string;
  maintainer_id: string;
  maintainer_node?: MaintainerPathNode;
  root_dir_name?: MaintainerPathNode;
  _key!: SymmKey;
  constructor(
    public collection_id: string,
    path_node: DirPathNode,
    public type: DriveEntryTypes = DriveEntryType.DIR,
    public default_collection_id: string = "", // NOTE: This is the root directory id of the default collection 
    drive_module: DriveConstantsType = DriveConstants.DEFAULT_DRIVE
  ) {
    this.id = path_node.getId_asB64();
    // this.collection_id = path_node.getCollectionId_asB64();  // use when BACK-1318 gets updated in the collection_pb.ts and deployed into prod
    this.maintainer_id = path_node.getMaintainerId_asB64();
    this.deleted = path_node.getIsDeleted();
    this.wrapped_dir_key = path_node.getWrappedDirKey();
    this.key_version = this.wrapped_dir_key?.getKeyVersion();
    this.wrapped_name = path_node.getName_asU8();
    this.path = path_node.getName_asB64();
    this.url = drive_module === DriveConstants.WATCHED ? buildWatchedNavigationLink(collection_id, this.id) :
      //  drive_module === DriveConstants.TRASH_DRIVE ? buildTrashNavigationLink:  // ***** TBD: NEED TO INTEGRATE ***** //
      (default_collection_id === this.id ? DefaultRoutes.drive_root : buildNavigationLink(collection_id, this.id, type));

    // NOTE: This node is the maintainer node (acl nodes) -> this.id === this.maintainer_id
    if (!!path_node.hasMaintainerScopedName()) {
      const scoped_name = path_node.getMaintainerScopedName();
      this.maintainer_node = {
        scoped_name,
        key_version: scoped_name?.getKeyVersion(),
        content: scoped_name?.getContent_asU8()
      };
    }
    if (!!path_node.hasRootDirName()) {
      const scoped_name = path_node.getRootDirName();
      this.root_dir_name = {
        scoped_name,
        key_version: scoped_name?.getKeyVersion(),
        content: scoped_name?.getContent_asU8()
      };
    }
  }

  get node(): PathNode {
    return {
      id: this.id,
      collection_id: this.collection_id,
      path: this.path,
      url: this.url,
      deleted: this.deleted,
      wrapped_name: this.wrapped_name,
      wrapped_dir_key: this.wrapped_dir_key,
      key_version: this.key_version,
      maintainer_id: this.maintainer_id,
      maintainer_node: this.maintainer_node,
      root_dir_name: this.root_dir_name
    };
  }
}
